import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Plus, Pencil, Trash2, X } from 'lucide-react';
import api from '../../utils/api';
import { parseDate } from '../../utils/dates';
import './BlackoutDates.css';

function BlackoutDates() {
  const [blackouts, setBlackouts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    reason: '',
    department_id: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [blackoutsRes, deptsRes] = await Promise.allSettled([
        api.get('/blackouts'),
        api.get('/departments'),
      ]);
      setBlackouts(blackoutsRes.status === 'fulfilled' ? (blackoutsRes.value.blackouts || []) : []);
      setDepartments(deptsRes.status === 'fulfilled' ? (deptsRes.value.departments || []) : []);
    } catch (err) {
      // Don't loop
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason || null,
        department_id: formData.department_id || null,
      };

      if (editing) {
        await api.put(`/blackouts/${editing.id}`, payload);
        showFeedback('Gray date period updated');
      } else {
        await api.post('/blackouts', payload);
        showFeedback('Gray date period created');
      }

      resetForm();
      fetchData();
    } catch (err) {
      showFeedback(err.message || 'Failed to save gray date period', 'error');
    }
  };

  const handleEdit = (blackout) => {
    setEditing(blackout);
    setFormData({
      name: blackout.name,
      start_date: blackout.start_date?.substring(0, 10) || '',
      end_date: blackout.end_date?.substring(0, 10) || '',
      reason: blackout.reason || '',
      department_id: blackout.department_id || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (blackout) => {
    try {
      await api.delete(`/blackouts/${blackout.id}`);
      showFeedback(`"${blackout.name}" deleted`);
      setConfirmDelete(null);
      fetchData();
    } catch (err) {
      showFeedback('Failed to delete gray date period', 'error');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormData({
      name: '',
      start_date: '',
      end_date: '',
      reason: '',
      department_id: '',
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return parseDate(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isActive = (blackout) => {
    const today = new Date().toISOString().substring(0, 10);
    return blackout.end_date >= today;
  };

  return (
    <div className="blackout-page">
      <div className="blackout-header">
        <div className="blackout-header-left">
          <ShieldAlert size={22} strokeWidth={2} />
          <h1>Gray Dates</h1>
        </div>
        <button className="toolbar-btn primary" onClick={() => setShowForm(true)}>
          <Plus size={14} />
          Add Period
        </button>
      </div>

      <div className="blackout-notice">
        Gray dates are dates where time off is discouraged but not blocked. Employees can still submit time off requests during these periods, but they will receive a warning.
      </div>

      {feedback && (
        <div className={`blackout-feedback ${feedback.type}`}>
          {feedback.message}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="blackout-form-container">
          <div className="blackout-form-header">
            <h3>{editing ? 'Edit Gray Date Period' : 'Add Gray Date Period'}</h3>
            <button className="icon-btn" onClick={resetForm}>
              <X size={16} />
            </button>
          </div>
          <form className="blackout-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="e.g., Year-End Freeze, Client Deadline"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(f => ({ ...f, start_date: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(f => ({ ...f, end_date: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Department (optional)</label>
              <select
                value={formData.department_id}
                onChange={(e) => setFormData(f => ({ ...f, department_id: e.target.value }))}
              >
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <span className="form-hint">Leave blank to apply to all departments</span>
            </div>

            <div className="form-group">
              <label>Reason (optional)</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData(f => ({ ...f, reason: e.target.value }))}
                placeholder="Why is this period restricted?"
                rows={2}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
              <button type="submit" className="btn-primary">
                {editing ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="blackout-loading">Loading gray dates...</div>
      ) : blackouts.length === 0 ? (
        <div className="blackout-empty">
          <ShieldAlert size={40} strokeWidth={1.5} />
          <p>No gray dates defined.</p>
          <p className="blackout-empty-hint">
            Create gray date periods to suggest dates when employees should avoid taking time off.
          </p>
        </div>
      ) : (
        <div className="blackout-list">
          {blackouts.map(b => (
            <div key={b.id} className={`blackout-card ${isActive(b) ? 'active' : 'past'}`}>
              <div className="blackout-card-main">
                <div className="blackout-card-info">
                  <div className="blackout-card-name">
                    {b.name}
                    {!isActive(b) && <span className="past-badge">Past</span>}
                  </div>
                  <div className="blackout-card-dates">
                    {formatDate(b.start_date)} - {formatDate(b.end_date)}
                  </div>
                  {b.department_name && (
                    <div className="blackout-card-dept">{b.department_name}</div>
                  )}
                  {!b.department_id && (
                    <div className="blackout-card-dept">All Departments</div>
                  )}
                  {b.reason && (
                    <div className="blackout-card-reason">{b.reason}</div>
                  )}
                </div>
                <div className="blackout-card-actions">
                  <button className="icon-btn" onClick={() => handleEdit(b)} title="Edit">
                    <Pencil size={14} />
                  </button>
                  <button className="icon-btn danger" onClick={() => setConfirmDelete(b)} title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="blackout-card-meta">
                Created by {b.created_by_name || 'Admin'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Gray Date Period</h3>
            <p>Are you sure you want to delete <strong>{confirmDelete.name}</strong>?</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(confirmDelete)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlackoutDates;
